import { QueryClientContext } from "../../context/QueryClientContext";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { RHFPasswordInput } from "./PasswordInput";
import { RHFPhoneInput, RHFTextInput } from "./TextInput";
import { useRegister } from "../../api/useAuth";
import { useState } from "react";
import { lang } from "../../utilities/lang";
import ReactFlagsSelect from "react-flags-select";
import { AxiosError } from "axios";
import { getResponseError } from "../../helpers/helpers";
import { PasswordReset } from "./PasswordReset";

export type RegisterFormType = {
    firstname?: string;
    lastname?: string;
    email?: string;
    tel1?: string;
    tel2?: number;
    street?: string;
    number?: string;
    postcode?: string;
    city?: string;
    country?: string;
    password?: string;
    company?: string;
    password_confirmation?: string;
};

export type RegisterProps = {
    dashboardLink: string;
};

export const Register = (props: RegisterProps) => {
    return (
        <QueryClientContext>
            <Content {...props} />
        </QueryClientContext>
    );
};

const Content = (props: RegisterProps) => {
    const { register, trigger, getValues, setError, control, formState, handleSubmit } = useForm<RegisterFormType>({
        mode: "onBlur",
        defaultValues: { tel1: "43", country: "AT" },
    });
    const [error, setComponentError] = useState<string>("");
    const from = new URLSearchParams(window.location.search).get("from") ?? null;
    const registerUser = useRegister();

    const onSubmit: SubmitHandler<RegisterFormType> = async (data) => {
        setComponentError("");
        try {
            const resp = await registerUser.mutateAsync(data);
            if (resp.status === 201) {
                window.location.href = from === "cart" ? lang.get("cart.url") : props.dashboardLink;
            } else {
                setComponentError(lang.get("entry.formError"));
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (e) {
            const axiosError = e as AxiosError;
            const errors = getResponseError(axiosError);
            errors.forEach((error) => {
                if (error.key) {
                    // @ts-ignore
                    setError(error.key, { message: error.error });
                } else {
                    setComponentError(error.error);
                }
            });
        }
    };

    return (
        <>
            <h1>{lang.get("entry.formHeading")}</h1>
            <div>{lang.get("entry.registerText")}</div>
            {error ? <div className={"error"}>{error}</div> : null}
            <form onSubmit={handleSubmit(onSubmit)}>
                <RHFTextInput
                    label={lang.get("Vorname") || "Vorname"}
                    formState={formState}
                    register={register}
                    trigger={trigger}
                    required={true}
                    fieldName={"firstname"}
                />
                <RHFTextInput
                    label={lang.get("Nachname") || "Nachname"}
                    formState={formState}
                    register={register}
                    trigger={trigger}
                    required={true}
                    fieldName={"lastname"}
                />
                <RHFTextInput
                    label={lang.get("Email") || "Email"}
                    formState={formState}
                    register={register}
                    trigger={trigger}
                    required={true}
                    fieldName={"email"}
                    pattern={{
                        value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                        message: lang.get("Ungültige Email Adresse") || "Ungültige Email Adresse",
                    }}
                />

                <RHFPhoneInput
                    label={lang.get("Telefonnummer") || "Telefonnummer"}
                    formState={formState}
                    register={register}
                    trigger={trigger}
                    control={control}
                    required={true}
                    fieldName={"tel2"}
                />

                <div className="combination">
                    <RHFTextInput
                        label={lang.get("Straße") || "Straße"}
                        formState={formState}
                        register={register}
                        trigger={trigger}
                        required={true}
                        fieldName={"street"}
                    />
                    <RHFTextInput
                        label={lang.get("Nummer") || "Nummer"}
                        formState={formState}
                        register={register}
                        trigger={trigger}
                        required={true}
                        fieldName={"number"}
                    />
                </div>
                <div className="combination">
                    <RHFTextInput
                        label={lang.get("Postleitzahl") || "Postleitzahl"}
                        formState={formState}
                        register={register}
                        trigger={trigger}
                        required={true}
                        fieldName={"postcode"}
                    />
                    <RHFTextInput
                        label={lang.get("Stadt") || "Stadt"}
                        formState={formState}
                        register={register}
                        trigger={trigger}
                        required={true}
                        fieldName={"city"}
                    />
                </div>
                <label className={"country-input"} aria-label={lang.get("Land") || "Land"}>
                    <Controller
                        name={"country"}
                        control={control}
                        render={({ field }) => (
                            <ReactFlagsSelect
                                className={"flag-select"}
                                countries={["AT", "CH", "DE", "IT", "GB", "FR"]}
                                customLabels={{
                                    AT: lang.get("Österreich"),
                                    CH: lang.get("Schweiz"),
                                    DE: lang.get("Deutschland"),
                                    IT: lang.get("Italien"),
                                    GB: lang.get("Großbritannien"),
                                    FR: lang.get("Frankreich"),
                                }}
                                selected={field.value}
                                onSelect={async (code) => {
                                    field.onChange(code);
                                    await trigger("country");
                                }}
                                rfsKey={"country-code"}
                            />
                        )}
                    />
                    {formState.errors["country"] && (
                        <span className={"error"}>{formState.errors["country"].message}</span>
                    )}
                </label>
                <RHFTextInput
                    label={lang.get("Firma")}
                    formState={formState}
                    register={register}
                    trigger={trigger}
                    fieldName={"company"}
                    required={false}
                />
                <div className={"combination row"}>
                    <RHFPasswordInput
                        label={lang.get("Passwort") || "Passwort"}
                        formState={formState}
                        register={register}
                        getValues={getValues}
                        trigger={trigger}
                        fieldName={"password"}
                    />
                    <RHFPasswordInput
                        label={lang.get("Passwort wiederholen") || "Passwort wiederholen"}
                        formState={formState}
                        register={register}
                        getValues={getValues}
                        trigger={trigger}
                        fieldName={"password_confirmation"}
                    />
                </div>
                <label>
                    <span className={"privacy-statement"}>
                        {lang.get("Ja, ich habe die ")}
                        <a className={"white underline"} href={lang.get("privacy.url")} target={"_blank"}>
                            {lang.get("Datenschutzerklärung")}
                        </a>
                        {lang.get(
                            " zur Kenntnis genommen und erteile die Einwilligung für die Erhebung und Nutzung meiner eingegebenen Daten."
                        )}
                    </span>
                    <input type="checkbox" required={true} />
                </label>
                <button className={`button submit ${registerUser.isPending ? "loading" : ""}`} type="submit">
                    {lang.get("Registrieren")}
                </button>
                <div className="login">
                    {lang.get("entry.loginText")}
                    <a className="underline half white" href={lang.get("login.url")}>
                        {lang.get("login.title")}
                    </a>
                </div>
            </form>
            <PasswordReset />
        </>
    );
};
